import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    document.addEventListener('click', this.#handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    document.removeEventListener('click', this.#handleOutsideClick);

    // If the dialog was open when removed from the DOM (e.g. Klevu re-rendering
    // the SERP template), clean up the global state that closeDialog would have handled.
    const { dialog } = this.refs;
    const isOpen =
      this.id === 'search-modal'
        ? dialog?.classList.contains('open')
        : dialog?.open;

    if (isOpen) {
      document.body.style.overflowY = '';
      document.documentElement.style.overflowY = '';
      document.documentElement.removeAttribute('scroll-lock');
      const backdrop = document.querySelector('#value-prop-link-slideout-mask');
      if (backdrop) {
        backdrop.setAttribute('data-active', 'false');
        backdrop.classList.remove('active');
      }
    }
  }

  #handleOutsideClick = (e) => {
    const { dialog } = this.refs;

    const isOpen =
      this.id === 'search-modal'
        ? dialog.classList.contains('open')
        : dialog.open;

    if (!isOpen) return;

    // Ignore button clicks that actually trigger the dialog opening
    if (e.target.closest('[on\\:click*="showDialog"]')) {
      return;
    }

    // Ignore clicks from within the dialog
    if (dialog.contains(e.target) || this.contains(e.target)) {
      return;
    }

    // Handles the backdrop/mask logic
    const backdrop = document.querySelector('#value-prop-link-slideout-mask');
    const isBackdropActive = backdrop?.getAttribute('data-active') === 'true';

    if (
      isBackdropActive &&
      !e.target.closest('cart-drawer-component') &&
      e.isTrusted
    ) {
      this.closeDialog();
    }
  };

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;

    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  setPosition(drawerInner) {
    const paddingAmount = 24;

    this.refs.dialog.style.top = `${paddingAmount}px`;
    // Padding * 2 for top and bottom
    this.refs.dialog.style.setProperty(
      '--cart-max-height',
      `calc(100dvh - ${paddingAmount * 2}px)`,
    );
    // 2 * 24px margin outside, 2 * 24px vertical padding inside, so 24 * 4
    drawerInner?.style?.setProperty(
      '--cart-inner-max-height',
      `calc(100dvh - ${paddingAmount * 4}px)`,
    );
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    if (
      (this.id !== 'search-modal' && dialog.open) ||
      (this.id == 'search-modal' && dialog.classList.contains('open'))
    ) {
      return;
    }

    if (this.id == 'search-modal') {
      const searchInput = this.querySelector('#cmdk-input');
      if (searchInput) {
        setTimeout(() => searchInput.focus(), 0);
      }
      dialog.classList.add('open');
      document.body.style.overflowY = 'clip';
      document.documentElement.style.overflowY = 'clip';
    } else {
      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      const drawerInner = this.querySelector('#cart-drawer-inner');
      const backdrop = document.querySelector('#value-prop-link-slideout-mask');

      if (backdrop) {
        backdrop.setAttribute('data-active', 'true');
        backdrop.classList.add('active');
      }

      if (drawerInner) {
        this.setPosition(drawerInner);
      }
    }

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (
      (this.id !== 'search-modal' && !dialog.open) ||
      (this.id == 'search-modal' && !dialog.classList.contains('open'))
    ) {
      return;
    }
    if (this.id == 'search-modal') {
      const searchInput = this.querySelector('#cmdk-input');
      if (searchInput) {
        searchInput.blur();
      }
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.overflowY = '';
    document.documentElement.style.overflowY = '';
    const backdrop = document.querySelector('#value-prop-link-slideout-mask');
    if (backdrop) {
      backdrop.setAttribute('data-active', 'false');
      backdrop.classList.remove('active');
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    if (this.id == 'search-modal') {
      dialog.classList.remove('open');
    } else {
      dialog.close();
    }

    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component'))
  customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (
      event.target instanceof HTMLDialogElement ||
      event.target instanceof HTMLDetailsElement
    ) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true },
);
